import Card from 'components/card/Card'
import { Grid } from '@mui/material'
import { NewTable } from 'components/datatable/NewTable'
import { exportPictureIcon } from 'components/echart/EChartUtils'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import SelectionSelect from 'components/forms/specific/SelectionSelect'
import ReactEcharts from 'echarts-for-react'
import { every, uniq } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import { getDate } from 'utils/DateUtil'
import { getLabel } from 'utils/StoreUtils'
import { normalizeString, substringText } from 'utils/StringUtil'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { THRESHOLD_COLORS_CODES, whiteColor } from 'utils/constants/ColorTheme'

const TABLE_HEADERS = ['code', 'name', 'nullValue']
const TABLE_NO_CODE_HEADERS = ['name', 'nullValue']

const Graph = ({
    selected = [],
    analysis = [],
    parameterKey = 'parameter',
    resultKey = 'result',
}) => {
    const {
        parameters,
        units,
        operation,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        operation: store.OperationReducer.operation,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const filteredAnalysis = analysis.filter(({ [parameterKey]: parameter }) => selected.includes(`${parameter}`))

    const data = filteredAnalysis.map(({ [resultKey]: result, [parameterKey]: parameter, unit }) => {
        const { labelWithCode = '', shortLabel = '', name } = parameters.find(({ code }) => `${parameter}` === code) || {}
        return {
            value: result,
            parameter: showSandreCode ? labelWithCode : name,
            paramShortLabel: shortLabel || name,
            unit: getLabel(units, unit, 'symbol'),
        }
    })
    const options = {
        series: [{
            type: 'bar',
            data,
            areaStyle: {
                normal: {
                    color: 'blue',
                    opacity: 1,
                },
            },
            itemStyle: {
                normal: {
                    color: 'blue',
                    opacity: 1,
                },
            },
        }],
        xAxis: [{
            type: 'category',
            data: data.map(({ paramShortLabel, unit }) => `${paramShortLabel} ${unit}`),
            axisTick: {
                alignWithLabel: true,
            },
            axisLabel: {
                rotate: 50,
            },
        }],
        yAxis: [{
            type: 'value',
            axisPointer: {
                show: true,
                type: 'line',
            },
        }],
        tooltip: {
            formatter: ([, { data: { parameter, value, unit } }]) => `${parameter}</br>${value} ${unit}`,
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
            },
        },
        grid: {
            top: '8%',
            left: '3%',
            right: '4%',
            bottom: '80',
            containLabel: true,
        },
        toolbox: {
            right: '4%',
            top: '2%',
            feature: {
                saveAsImage: {
                    title: i18n.export,
                    icon: exportPictureIcon,
                    name: `operation_${getDate(operation.dateStart)}`,
                },
            },
        },
        name: i18n.analysis,
    }
    return (
        <ReactEcharts
            option={options}
            style={{ minHeight: '70vh' }}
            notMerge
        />
    )
}

Graph.propTypes = {
    selected: PropTypes.arrayOf(PropTypes.number),
    analysis: PropTypes.arrayOf(PropTypes.shape({})),
    parameterKey: PropTypes.string,
    resultKey: PropTypes.string,
}

const OperationGraph = ({
    analysis = [],
    parameterKey = 'parameter',
    resultKey = 'result',
    hideDataType = false,
}) => {
    const {
        parameters,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)
    const [dataType, setDataType] = useState()
    const [selection, setSelection] = useState()
    const [listParam, setListParam] = useState([])
    const [searchValue, setSearchValue] = useState('')
    const [selected, setSelected] = useState(() => analysis.map(({ [parameterKey]: parameter }) => `${parameter}`))

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const parameterList = useMemo(() => {
        const parametersFromAnalysis = analysis.map(({ [parameterKey]: parameter }) => parameters.find(({ code }) => code === `${parameter}`)).filter(p => !!p)
        const selectionParameters = selection !== '-1' ? listParam.map(param => parametersFromAnalysis.find(({ code }) => code === param)).filter(p => !!p) : parametersFromAnalysis
        const searchValueFormated = normalizeString(searchValue)
        return searchValueFormated ? selectionParameters.filter(({ code = '', name = '', mnemonique = '' }) => normalizeString(`${code}${name}${mnemonique}`).includes(searchValueFormated)) : selectionParameters
    }, [analysis, listParam, parameterKey, parameters, searchValue, selection])

    const formatParameters = parameterList.map(({ code = '', name = '' }) => {
        const isSelected = !!selected.find(param => param === code)
        const tooltip = name.length > 40 ? { tooltip: name, className: 'tooltipped' } : {}
        return {
            code: { value: code },
            name: { value: substringText(name, 40), ...tooltip },
            nullValue: { value: <i className={'material-icons'} >{isSelected ? 'check_box' : 'check_box_outline_blank'}</i> },
            isSelected,
            lineColor: isSelected ? THRESHOLD_COLORS_CODES.LIGHT_BLUE : whiteColor,
        }
    })

    const getFilteredParametersFromAnamysis = type => {
        switch (type) {
            case 'detected': return analysis.filter(({ remarkCode }) => remarkCode !== '0' && remarkCode !== '2').map(({ [parameterKey]: parameter }) => parameter)
            case 'quantified': return analysis.filter(({ remarkCode }) => remarkCode !== '0' && remarkCode !== '2' && remarkCode !== '10').map(({ [parameterKey]: parameter }) => parameter)
            default: return analysis.map(({ [parameterKey]: parameter }) => parameter)
        }
    }

    const checkAll = () => {
        setSelected(uniq([...selected, ...formatParameters.map(({ code: { value: code } }) => code)]))
    }

    const uncheckAll = () => {
        setSelected(selected.filter(param => !formatParameters.find(({ code: { value: code } }) => param === code)))
    }

    const actions = every(formatParameters, ({ isSelected }) => isSelected) ? [{
        onClick: uncheckAll,
        iconName: 'check_box_outline_blank',
        tooltip: i18n.unselectAll,
    }] : [{
        onClick: checkAll,
        iconName: 'check_box',
        tooltip: i18n.selectAll,
    }]

    const dataTypes = [
        {
            code: 'all',
            label: i18n.allData,
        },
        {
            code: 'detected',
            label: i18n.detectedData,
        },
        {
            code: 'quantified',
            label: i18n.quantifiedData,
        },
    ]

    return (
        <Grid container>
            <Grid size={3} sx={{ margin: 0 }}>
                <Grid container sx={{ padding: 0, marginBottom: '20px' }}>
                    <Grid size={12}>
                        {
                            !hideDataType && (
                                <Grid container sx={{ paddingTop: 1 }}>
                                    <Grid size={12}>
                                        <Select
                                            label={i18n.dataTypes}
                                            options={dataTypes}
                                            value={dataType}
                                            onChange={v => {
                                                setDataType(v)
                                                setSelected(getFilteredParametersFromAnamysis(v))
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            )
                        }
                        <Grid container sx={hideDataType ? { paddingTop: 1 } : {}}>
                            <Grid size={12}>
                                <SelectionSelect
                                    value={selection}
                                    onChange={(newListParam, newSelection) => {
                                        setSelection(newSelection)
                                        setListParam(newListParam)
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid size={12}>
                                <Input
                                    title={i18n.search}
                                    value={searchValue}
                                    onChange={setSearchValue}
                                />
                            </Grid>
                        </Grid>
                        <Card
                            title={`${i18n.analyzedParameters} (${selected.length})`}
                            actions={actions}
                        >
                            <Grid container>
                                <Grid size={12}>
                                    <NewTable
                                        rows={formatParameters}
                                        headers={showSandreCode ? TABLE_HEADERS : TABLE_NO_CODE_HEADERS}
                                        maxHeight='40vh'
                                        onClickRow={({ code: { value: code }, isSelected }) => setSelected(isSelected ? selected.filter(param => param !== code) : [...selected, code])}
                                        rowsPerPageOptions={nbPerPageLabel}
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>
            <Grid size={9}>
                <Graph
                    selected={selected}
                    analysis={analysis}
                    parameterKey={parameterKey}
                    resultKey={resultKey}
                />
            </Grid>
        </Grid>
    )
}

OperationGraph.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({})),
    parameterKey: PropTypes.string,
    resultKey: PropTypes.string,
    hideDataType: PropTypes.bool,
}

export default OperationGraph